// The silent sign-in to one circle (36-desyr-id.md, Behaviour 2.2, v1.58e): a single-use record
// for the hop; step A finds the account service's sign-in in the background; step B goes through
// the circle's desyr ID provider in this window; step C comes back to `/login?desyrHop=…&loginToken=…`;
// the token is used only at the chat server of the circle in the record.
import type { Config } from '../core/config';
import { random } from '../core/mail';
import { readJson, writeJson } from '../core/storage';
import { readSession, writeSession, type Session } from '../core/session';
import { askUnlock, freshDb, keepWaiting, signedInAccount, storePrefix, waiting } from '../accounts/store';
import { pageWaiting, read } from '../signin/account';
import { companyEntry, discover, found } from '../signin/company';
import { login, reach } from '../signin/server';
import { authGen, keepQueue, readAfter, readId, readQueue } from './store';

const HOP = 'desyr_id_hop';
interface Hop {
  nonce: string;
  server: string;
  at: number;
  used?: boolean;
}
/** The current hop: unused, under 5 minutes old. */
const liveHop = () => {
  const hop = readJson<Hop>(HOP);
  return hop && !hop.used && Date.now() - hop.at < 5 * 60_000 ? hop : undefined;
};
/** A hop started for this circle more than 30 s ago that never came back: it failed (rule 7). */
export const hopTimedOut = (server: string) => {
  const hop = readJson<Hop>(HOP);
  return !!hop && !hop.used && hop.server === server && Date.now() - hop.at > 30_000;
};

/**
 * A sign-in run is under way while its last hop is under 10 minutes old (the queue's own time): a
 * start then goes on with the queue as it is, so a circle is navigated to at most once per run, also
 * across reloads; only Retry or a fresh desyr ID sign-in (`newRun`) starts it again.
 */
export const runUnderWay = () => {
  const hop = readJson<Hop>(HOP);
  return !!hop && Date.now() - hop.at < 10 * 60_000;
};
export const newRun = () => writeJson(HOP, undefined);
/** A sign-out marks the hop used (36, Behaviour 4.4.1, v1.58.63): its return finds nothing to take. */
export function hopUsed(): void {
  const record = readJson<Hop>(HOP);
  if (record) writeJson(HOP, { ...record, used: true });
}

/** The circle's account service: config.json's entry for its server name, else discovery for the name. */
async function entryOf(config: Config, server: string) {
  const known = companyEntry(config, server);
  if (known) return known;
  const found = await discover(config, server);
  return found.kind === 'found' ? companyEntry(config, found.server) : undefined;
}

/** One circle's hop: the record, step A, then step B (a navigation) or step C (in the background). */
/** `join`: the hop of a Join (37), the one that may go through the account service's deleted-account page. */
export async function hop(config: Config, server: string, join = false): Promise<void> {
  const entry = await entryOf(config, server);
  if (!entry?.mas) throw new Error('no account service');
  const nonce = random(16);
  writeJson(HOP, { nonce, server, at: Date.now() } satisfies Hop);
  const mas = entry.mas.replace(/\/+$/, '');
  const back = `${location.origin}/login?desyrHop=${nonce}`;
  const start = `${mas}/_matrix/client/v3/login/sso/redirect?redirectUrl=${encodeURIComponent(back)}`;
  // Without a provider id: the account service's usual page way (04).
  if (!entry.desyrIdProvider) return location.assign(start);
  // Desyr ID as the only sign-in: the redirects go on to desyr ID's origin and the request fails;
  // then the whole window goes once to the same address (v1.58.5), through redirects only.
  const answer = await read(start).catch(() => undefined);
  if (!answer) {
    // Reachable at all? One side-effect-free probe (v1.58.9): no answer within 5 s, the circle failed, no navigation.
    const probe = await fetch(`${mas}/.well-known/openid-configuration`, { mode: 'no-cors', cache: 'no-store', signal: AbortSignal.timeout(5_000) }).catch(() => undefined);
    if (!probe) throw new Error('unreachable');
    return location.assign(start);
  }
  // Step B with the sign-in's id: from the address of the account service's sign-in page, or, with a
  // session still there (e.g. from the old sign-in), from the path of its "Continue" page, whose
  // forms are never sent (v1.58.14).
  const end = new URL(answer.url);
  const id = end.pathname === '/login' && end.searchParams.get('kind') === 'continue_compat_sso_login' ? end.searchParams.get('id') : /^\/complete-compat-sso\/([^/]+)$/.exec(end.pathname)?.[1];
  if (id) return location.assign(`${mas}/upstream/authorize/${encodeURIComponent(entry.desyrIdProvider)}?kind=continue_compat_sso_login&id=${encodeURIComponent(decodeURIComponent(id))}`);
  // A Join's hop on a page with nothing to continue (the account service's deleted-account page, a
  // rejoin after leaving): the window goes there and the page goes on by itself; the app presses
  // nothing, and desyr ID may ask once (v1.58.11). A silent start-up hop fails here instead.
  if (join) return location.assign(start);
  throw new Error('the account service asked something');
}

/**
 * Back at `/login` with a `loginToken` (rule 6): with a live hop's nonce, the token signs in that
 * hop's circle as an account of this device; a nonce with no record, a used or expired one: the
 * token is thrown away unused. The address is cleaned and the hop leaves the queue either way.
 * Back with the nonce but no token, that circle failed. A token without a nonce while signed in with
 * desyr ID is dropped unless 04's sign-in waits for it. False when the address is left to 04.
 */
export async function takeHop(): Promise<boolean> {
  const url = new URL(location.href);
  const nonce = url.searchParams.get('desyrHop');
  const token = url.searchParams.get('loginToken');
  if (location.pathname !== '/login') return false;
  // Without a nonce while signed in with desyr ID: used only by 04's own sign-in that waits for it,
  // otherwise dropped silently (v1.58.7).
  if (!nonce) return !!token && !!readId() && !pageWaiting() && (history.replaceState(null, '', '/login'), true);
  history.replaceState(null, '', '/login');
  const hop = liveHop();
  const record = readJson<Hop>(HOP);
  if (record) writeJson(HOP, { ...record, used: true });
  // Back without a token, or with a nonce that isn't the live hop's: that circle failed, never a second try.
  // The record's circle, or without a usable record the queue's head, leaves the queue for this run (v1.58.7).
  const failed = record?.server ?? readQueue()[0];
  if (!token || !hop || hop.nonce !== nonce) return (keepQueue(readQueue().filter((s) => s !== failed)), true);
  keepQueue(readQueue().filter((s) => s !== hop.server));
  const gen = authGen();
  const session = await reach(hop.server).then((base) => login(base, { type: 'm.login.token', token }, () => {})).catch(() => undefined);
  // Signed out meanwhile in another tab: the late answer stores nothing (Behaviour 4.4.3, v1.58.63).
  if (session && gen === authGen()) addAccount(session, readAfter()?.do === 'joined');
  return true;
}

/**
 * Back from the account service's error page (rule 8, v1.58.6): `/login?signInError=<account host>`.
 * The circle whose account service has that host failed (its record used up, out of the queue for
 * this run): "circle"; "general" when no circle matches (a general "Couldn't sign in"). The address
 * is cleaned at once.
 */
export function takeSignInError(config: Config): 'circle' | 'general' | undefined {
  const url = new URL(location.href);
  if (location.pathname !== '/login' || !url.searchParams.has('signInError')) return;
  const host = url.searchParams.get('signInError');
  url.searchParams.delete('signInError');
  history.replaceState(null, '', url.pathname + url.search);
  const hostOf = (mas?: string) => (mas ? URL.parse(mas)?.host : undefined);
  const server = host && Object.entries({ ...found(), ...config.desyrSignIn }).find(([, e]) => hostOf(e.mas) === host)?.[0];
  if (!server) return 'general';
  const record = readJson<Hop>(HOP);
  if (record) writeJson(HOP, { ...record, used: true });
  keepQueue(readQueue().filter((s) => s !== server));
  return 'circle';
}

/** The circle becomes an account of this device (33): the open one waits, the new one has its own databases. */
function addAccount(session: Session, keepOpen = false): void {
  const current = readSession();
  // Joined while in another circle: that one stays open, the new one waits with its own databases.
  if (keepOpen && current && current.user_id !== session.user_id)
    return (askUnlock(session.user_id), keepWaiting([...waiting().filter((a) => a.user_id !== session.user_id), { ...session, db: freshDb(session.device_id) }]));
  if (current && current.user_id !== session.user_id) keepWaiting([...waiting().filter((a) => a.user_id !== current.user_id), { ...current, db: storePrefix() }]);
  signedInAccount(session);
  writeSession(session);
}
